import React from 'react';
import { camelCase, startCase, memoize } from 'lodash';
import classNames from 'classnames';
import { InvoiceLineItemDiscount } from '@atlassian/cssxp-types-billing';
import { Promotion } from '@css-xp/graphtypes';
import {
  ColGroup,
  CustomDynamicResultTable,
  NoDataContentInTable,
  SearchResultColumn,
  TableBody,
  TableHead,
  useFeatureToggle,
  FormatCurrency,
} from '@css-xp/base';
import { STATSIG_FEATURE_GATES, ENABLE_DESIGN_ITERATION_1 } from '@css-xp/ppc-constants';
import { SUB_BENEFIT_TYPE, getReadableDiscountValue, getReadablePromotionValue } from '@css-xp/base/entitlement-utils';
import { InvoiceLineItemPPC } from '../invoice-page-utils';
import './invoice-view-adjustments-pane.scss';
import '../../../common/css/adjustment-item.scss';

export const emptyStateProps = {
  header: '',
  description: 'No adjustments found',
  imageUrl: '',
};

const getPromotionReasonCode = (promotion: Promotion | undefined, discount: InvoiceLineItemDiscount | null) => {
  const promoReasonFilter = promotion?.applicationReason?.customisable?.id?.anyOf?.filter(
    (applicationReason) => applicationReason?.id === discount?.reasonCode
  ) ?? [
    {
      id: promotion?.applicationReason?.id,
      readableName: promotion?.applicationReason?.readableName,
    },
  ];
  const promotionInvoiceNote =
    (promoReasonFilter?.length ? promoReasonFilter?.[0]?.readableName : promotion?.purpose?.invoiceNote) || '';
  const readableReasonName = startCase(camelCase(promotionInvoiceNote));
  return readableReasonName;
};

const getPromotionReasonForFlatDiscount = (discount: InvoiceLineItemDiscount | undefined) => {
  return (
    <div>
      {discount?.amount ? (
        `USD ${discount.amount.toFixed(2)} discount`
      ) : (
        <NoDataContentInTable testId="flat-discount-promotion-reason-no-data" />
      )}
    </div>
  );
};

const getPromotionReasonWithDescription = (
  promotion: Promotion,
  discounts: InvoiceLineItemDiscount[],
  isFlatDiscountSupportEnabled?: boolean
) => {
  const promotionBenefits = promotion?.benefits?.[0];
  const discount = discounts?.find((discount) => discount?.promotionId === promotion?.id);

  if (isFlatDiscountSupportEnabled && promotion?.title && promotionBenefits?.subBenefitType === SUB_BENEFIT_TYPE.FLAT) {
    return getPromotionReasonForFlatDiscount(discount);
  }

  return (
    <div data-testid="promotion-invoice-note">
      {promotion?.title && (
        <div>
          {discount?.percent ? discount?.percent + '% discount' : getReadablePromotionValue(promotion as Promotion)}
        </div>
      )}
    </div>
  );
};

const getPromotionValue = (
  promotion: Promotion,
  discount: InvoiceLineItemDiscount | null,
  isFlatDiscountSupportEnabled?: boolean
) => {
  const promotionBenefits = promotion?.benefits?.[0];
  if (isFlatDiscountSupportEnabled && promotionBenefits?.subBenefitType === SUB_BENEFIT_TYPE.FLAT) {
    return <NoDataContentInTable testId="flat-discount-no-data" />;
  }
  return (
    <div data-testid="promotion-invoice-note">
      {promotion?.title && (
        <div>{discount?.percent ? discount?.percent + '%' : getReadableDiscountValue(promotion as Promotion)}</div>
      )}
    </div>
  );
};

const getColumns = memoize((isDesignIteration1Enabled: boolean): SearchResultColumn[] => {
  if (isDesignIteration1Enabled) {
    return [];
  }

  const columns = [
    {
      name: 'Promotion type',
      customClassNames: 'col-30',
    },
    {
      name: 'Discount',
      customClassNames: 'col-20',
    },
    {
      name: 'Adjustments',
      customClassNames: 'col-20',
    },
    {
      name: 'Reason',
      customClassNames: 'col-20',
    },
    {
      name: 'Notes',
      customClassNames: 'col-30',
    },
    {
      name: '',
      customClassNames: 'col-2',
    },
  ];
  return columns;
});

const InvoiceViewAdjustmentPane = (invoiceItem: InvoiceLineItemPPC): JSX.Element | null => {
  const hasAdjustments = invoiceItem?.promotionItems?.filter((promotion) => promotion.title) ?? [];
  const discounts = invoiceItem?.discounts ?? [];
  const [isEnabled] = useFeatureToggle();
  const isFlatDiscountSupportEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_FLAT_DISCOUNT_SUPPORT);
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const renderDiscountValue = (promotion: any, index: number) => {
    return <>{getPromotionValue(promotion as Promotion, discounts[index] ?? null, isFlatDiscountSupportEnabled)}</>;
  };
  const renderPromotionType = (promotion: any) => {
    return (
      <>
        {promotion?.title ? (
          startCase(camelCase(promotion?.title))
        ) : (
          <NoDataContentInTable testId="promotion-title-no-data" />
        )}
      </>
    );
  };
  const renderAdjustmentValue = (index: number) => {
    return (
      <>
        {/* // FIXME :: Handle the case when multiple promotions can be added and different discounts will be there */}
        <FormatCurrency
          key={`${discounts[index]?.amount ?? 0}-${index}`}
          value={discounts[index]?.amount ?? 0}
          showCurrencyCode
          currency={invoiceItem?.currency}
        />
      </>
    );
  };
  const renderReasonValue = (promotion: any, index: number) => {
    return (
      <>
        {getPromotionReasonCode(promotion as Promotion, discounts[index] ?? null) ? (
          getPromotionReasonCode(promotion as Promotion, discounts[index] ?? null)
        ) : (
          <NoDataContentInTable testId="promotion-reason-no-data" />
        )}
      </>
    );
  };
  const renderNotesValue = (promotion: any) => {
    return <>{getPromotionReasonWithDescription(promotion as Promotion, discounts, isFlatDiscountSupportEnabled)}</>;
  };

  const columns = getColumns(isDesignIteration1Enabled);

  return (
    <div
      className={classNames('adjustments-line-items', { 'adjustments-line-items-popup': isDesignIteration1Enabled })}
    >
      {isDesignIteration1Enabled || <h2 className="ent-adjustments-sidebar">{invoiceItem?.entitlementNumber}</h2>}
      <CustomDynamicResultTable
        heading={isDesignIteration1Enabled ? '' : 'Discount details'}
        emptyStateProps={emptyStateProps}
        noResults={hasAdjustments.length <= 0}
        isLoading={false}
        error={''}
        testId={'invoice-view-adjustments-table'}
      >
        {columns.length > 0 && (
          <>
            <ColGroup columns={columns} />
            <TableHead columns={columns} />
          </>
        )}
        <TableBody>
          {invoiceItem?.promotionItems?.map((promotion, index) => (
            <React.Fragment key={index}>
              {isDesignIteration1Enabled ? (
                <tr className="adjustment-item-v2">
                  <td className="wrap-text-line">
                    <ul>
                      <li>Discount &#x2014; {renderDiscountValue(promotion, index)}</li>
                      <li>Promotion type &#x2014; {renderPromotionType(promotion)}</li>
                      <li>Adjustment &#x2014; {renderAdjustmentValue(index)}</li>
                      <li>Reason &#x2014; {renderReasonValue(promotion, index)}</li>
                      <li>Notes &#x2014; {renderNotesValue(promotion)}</li>
                    </ul>
                  </td>
                </tr>
              ) : (
                <tr className="adjustment-item line-item">
                  {/* Promotion type */}
                  <td className="wrap-text-line" data-testId="promotion-title">
                    {renderPromotionType(promotion)}
                  </td>
                  {/* Discount */}
                  <td>{renderDiscountValue(promotion, index)}</td>
                  {/* Adjustments */}
                  <td>{renderAdjustmentValue(index)}</td>
                  {/* Reason */}
                  <td className="wrap-text-line" data-testId="promotion-reason">
                    {renderReasonValue(promotion, index)}
                  </td>
                  <td>{renderNotesValue(promotion)}</td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </CustomDynamicResultTable>
    </div>
  );
};

export default React.memo(InvoiceViewAdjustmentPane);
